import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import type { Currency, UsageProductInput } from '@vy/types';
import { isEmptyArray, purge } from '@vy/utils';
import { flatMap } from 'lodash';
import React from 'react';
import { AddIcon } from '../../../../../../../components/icons/AddIcon';
import { useUsageProductsOrder } from '../../../../../../../hooks/use-usage-products-order';
import { UsageCharges } from '../../../UsageCharges/UsageCharges';
import { AddItemMenuButton } from '../../create-plan-page-tabs-components/AddItemMenuButton';
import { EmptyTab } from '../../create-plan-page-tabs-components/EmptyTab';
import { UsageChargesMenuItems } from './usage-charges-tab-content-components/UsageChargesMenuItems';

type UsageChargesTabContentProps = {
  usageProducts: UsageProductInput[];
  currency: Currency;
};

export const UsageChargesTabContent: React.FC<UsageChargesTabContentProps> = ({
  usageProducts,
  currency,
}) => {
  const [usageProductsOrder] = useUsageProductsOrder(usageProducts);

  const selectedMetersIds = purge(
    flatMap(usageProducts, ({ variants }) => variants.map(({ meterId }) => meterId)),
  );

  if (isEmptyArray(usageProducts)) {
    return (
      <EmptyTab
        title="Get Started with Usage-based fees"
        description="Define tier-based or volume-based pricing to align with your growing customer needs."
      >
        <AddItemMenuButton
          cta={
            <span className="flex items-center gap-x-2">
              <AddIcon fill="white" />
              <span>Set Items & Pricing</span>
            </span>
          }
          {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.usageCharges.addFeeButton)}
        >
          <UsageChargesMenuItems />
        </AddItemMenuButton>
      </EmptyTab>
    );
  }

  return (
    <div className="flex w-full flex-col gap-y-4">
      <div className="flex items-center justify-end">
        <AddItemMenuButton
          color="secondary"
          cta={
            <span className="flex items-center gap-x-2">
              <VyIcon
                icon="PlusMedium"
                style={{
                  transform: 'scale(0.8)',
                }}
              />
              <span>Add Item</span>
            </span>
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.usageCharges.addFeeButton)}
        >
          <UsageChargesMenuItems selectedMetersIds={selectedMetersIds} />
        </AddItemMenuButton>
      </div>

      <UsageCharges
        order={usageProductsOrder}
        usageProducts={usageProducts}
        editable
        currency={currency}
      />
    </div>
  );
};
